import constants from '@Common/Core/Constant';
import { ComponentType, ShowAllTimeIntervals } from '@Common/Enums/Enum';
import { MapComponent, MapPinComponent } from '@InstanceFactory';

import { ComponentChangeGroup } from '@View/Core/ComponentDescriptors/ComponentChangeGroup';
import { componentDescriptorFactory } from './ComponentDescriptorFactory';
import { enumerable } from '@Common/Core/CoreDecorator';

export class componentDescriptors {
    @enumerable
    public static alertMessagesGridDescriptor() {
        const param = {
            componentType: ComponentType.alertMessagesGrid,
            toolBarIcon: 'alerts-message.png',
            componentGroup: constants.componentGroups.gridComponents,
            refreshAfterCreated: true,
            componentToolBarCommands: [constants.commands.actionWizard]
        };
        return componentDescriptorFactory.createBaseComponentDescriptor(param);
    }

    @enumerable
    public static alertsGridDescriptor() {
        const param = {
            componentType: ComponentType.alertsGrid,
            toolBarIcon: 'alerts.png',
            componentGroup: constants.componentGroups.gridComponents
        };
        return componentDescriptorFactory.createBaseComponentDescriptor(param);
    }

    @enumerable
    public static bubbleDescriptor() {
        const param = {
            componentType: ComponentType.bubble,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(2, [strings.dimensionTitles.bubbles]),
            toolBarIcon: 'bubbleChart.png',
            highchartType: constants.highchartTypes.bubble,
            hasSeriesSettingsTab: true,
            seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(true, false, false)
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static buttonDescriptor() {
        const param = {
            componentType: ComponentType.button,
            componentGroup: constants.componentGroups.controlComponents,
            toolBarIcon: 'button.png',
            showTitleByDefault: false,
            defaultComponentWidth: 100,
            defaultComponentHeight: 40,
            componentToolBarCommands: [constants.commands.actionWizard],
            componentInstanceType: 'ButtonComponent'
        };
        return componentDescriptorFactory.createComponentDescriptor(param);
    }

    @enumerable
    public static lineDescriptor() {
        const param = {
            componentType: ComponentType.line,
            toolBarIcon: 'LineChart.png',
            showAllTimeIntervals: ShowAllTimeIntervals.internal,
            clientInstanceType: 'ExtendedChart',
            appearanceComponentType: ComponentType.extendedChart,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.xAxis,
                strings.dimensionTitles.lines
            ])
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static extendedChartDescriptor() {
        const param = {
            componentType: ComponentType.extendedChart,
            seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(true, null, false),
            clientInstanceType: 'ExtendedChart',
            appearanceComponentType: ComponentType.extendedChart,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.xAxis,
                strings.dimensionTitles.breakdown
            ])
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static barDescriptor() {
        const param = {
            componentType: ComponentType.bar,
            toolBarIcon: 'barChart.png',
            seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(true, null, false),
            clientInstanceType: 'ExtendedChart',
            appearanceComponentType: ComponentType.extendedChart,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [strings.dimensionTitles.xAxis])
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static waterfallDescriptor() {
        const param = {
            componentType: ComponentType.waterfall,
            toolBarIcon: 'waterfall.png',
            seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(true, null, false),
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [strings.dimensionTitles.bars]),
            highchartType: constants.highchartTypes.waterfall
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static pyramidDescriptor() {
        const param = {
            componentType: ComponentType.pyramid,
            toolBarIcon: 'pyramid.png',
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [strings.dimensionTitles.breakdown]),
            highchartType: constants.highchartTypes.pyramid,
            hasAxisAndBreakdownsTab: true,
            dataWizard: 'MetricData',
            hasSeriesSettingsTab: true,
            hasDrillDownOptionTab: true,
            seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(true, false, false)
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static funnelDescriptor() {
        const param = {
            componentType: ComponentType.funnel,
            toolBarIcon: 'funnel.png',
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [strings.dimensionTitles.breakdown]),
            highchartType: constants.highchartTypes.funnel,
            hasAxisAndBreakdownsTab: true,
            dataWizard: 'MetricData',
            hasSeriesSettingsTab: true,
            hasDrillDownOptionTab: true,
            seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(true, false, false)
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static boxPlotDescriptor() {
        const param = {
            componentType: ComponentType.boxPlot,
            toolBarIcon: 'boxplot.png',
            seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(true, null, false),
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [strings.dimensionTitles.lines]),
            highchartType: constants.highchartTypes.boxPlot,
            hasAxisAndBreakdownsTab: true,
            hasDrillDownOptionTab: true,
            dataWizard: 'MetricData',
            clientInstanceType: 'BoxPlotChart'
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    private static polarChartBasicSetting = {
        toolBarIcon: 'polar.png',
        hasAxisAndBreakdownsTab: true,
        changableGroup: ComponentChangeGroup.polarGroup,
        seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(true, null, false),
        hasDrillDownOptionTab: true,
        appearance: {
            defaultLabelRotation: 0
        }
    };

    @enumerable
    public static polarChartColumnDescriptor() {
        let param = {
            componentType: ComponentType.polarChartColumn,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.xAxis,
                strings.dimensionTitles.bars
            ]),
            highchartType: constants.highchartTypes.column
        };

        param = $.extend(param, componentDescriptors.polarChartBasicSetting);
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static polarChartLineDescriptor() {
        const lineDimentions = [strings.dimensionTitles.xAxis, strings.dimensionTitles.lines];
        let param = {
            componentType: ComponentType.polarChartLine,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, lineDimentions),
            highchartType: constants.highchartTypes.line
        };
        param = $.extend(param, componentDescriptors.polarChartBasicSetting);
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static polarChartAreaDescriptor() {
        const areaDimentions = [strings.dimensionTitles.xAxis, strings.dimensionTitles.areas];
        let param = {
            componentType: ComponentType.polarChartArea,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, areaDimentions),
            highchartType: constants.highchartTypes.area
        };
        param = $.extend(param, componentDescriptors.polarChartBasicSetting);
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static polarChartScatterDescriptor() {
        let param = {
            componentType: ComponentType.polarChartScatter,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.xAxis,
                strings.dimensionTitles.breakdown
            ]),
            highchartType: constants.highchartTypes.scatter
        };
        param = $.extend(param, componentDescriptors.polarChartBasicSetting);
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static rangeBarDescriptor() {
        const param = {
            componentType: ComponentType.rangeBar,
            hasSeriesSettingsTab: true,
            seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(true, null, false),
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [strings.dimensionTitles.bars]),
            highchartType: constants.highchartTypes.mirrorrange
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static paretoDescriptor() {
        const param = {
            componentType: ComponentType.pareto,
            toolBarIcon: 'paretoChart.png',
            seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(true, null, false),
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [strings.dimensionTitles.bars]),
            highchartType: constants.highchartTypes.column
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static groupBarDescriptor() {
        const param = {
            componentType: ComponentType.groupBar,
            seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(true, null, false),
            clientInstanceType: 'ExtendedChart',
            appearanceComponentType: ComponentType.extendedChart,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.groups,
                strings.dimensionTitles.bars
            ])
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static trellisChartDescriptor() {
        const param = {
            componentType: ComponentType.trellisChart,
            componentToolBarCommands: [constants.commands.dataWizard, constants.commands.actionWizard],
            toolBarIcon: 'TrellisChart.png',
            seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(true, null, false),
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, []),
            componentInstanceType: 'TrellisChart',
            componentGroup: constants.componentGroups.chartComponents
        };
        return componentDescriptorFactory.createComponentDescriptor(param);
    }

    @enumerable
    public static stackedBarDescriptor() {
        const param = {
            componentType: ComponentType.stackedBar,
            toolBarIcon: 'stackBarChart.png',
            seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(true, null, false),
            clientInstanceType: 'ExtendedChart',
            appearanceComponentType: ComponentType.extendedChart,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.xAxis,
                strings.dimensionTitles.stackedBars
            ])
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static stackedBar100Descriptor() {
        const param = {
            componentType: ComponentType.stackedBar100,
            seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(true, null, false),
            isPercentComponent: true,
            clientInstanceType: 'ExtendedChart',
            appearanceComponentType: ComponentType.extendedChart,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.xAxis,
                strings.dimensionTitles.stackedBars100
            ])
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static areaDescriptor() {
        const param = {
            componentType: ComponentType.area,
            toolBarIcon: 'areaChart.png',
            showAllTimeIntervals: ShowAllTimeIntervals.internal,
            clientInstanceType: 'ExtendedChart',
            appearanceComponentType: ComponentType.extendedChart,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.xAxis,
                strings.dimensionTitles.areas
            ])
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static areaRangeDescriptor() {
        const param = {
            componentType: ComponentType.areaRange,
            toolBarIcon: 'areaChart.png',
            showAllTimeIntervals: ShowAllTimeIntervals.internal,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.xAxis,
                strings.dimensionTitles.areas
            ]),
            highchartType: constants.highchartTypes.arearange,
            clientInstanceType: 'AreaRangeChart'
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static invertedAreaDescriptor() {
        const param = {
            componentType: ComponentType.invertedArea,
            toolBarIcon: 'areaChart.png',
            showAllTimeIntervals: ShowAllTimeIntervals.internal,
            clientInstanceType: 'ExtendedChart',
            appearanceComponentType: ComponentType.extendedChart,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.xAxis,
                strings.dimensionTitles.areas
            ])
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static splineDescriptor() {
        const param = {
            componentType: ComponentType.spline,
            showAllTimeIntervals: ShowAllTimeIntervals.internal,
            clientInstanceType: 'ExtendedChart',
            appearanceComponentType: ComponentType.extendedChart,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.xAxis,
                strings.dimensionTitles.splines
            ])
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static invertedSplineDescriptor() {
        const param = {
            componentType: ComponentType.invertedSpline,
            toolBarIcon: 'data_annotation.png',
            showAllTimeIntervals: ShowAllTimeIntervals.internal,
            clientInstanceType: 'ExtendedChart',
            appearanceComponentType: ComponentType.extendedChart,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.xAxis,
                strings.dimensionTitles.splines
            ])
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static splineAreaDescriptor() {
        const param = {
            componentType: ComponentType.splineArea,
            showAllTimeIntervals: ShowAllTimeIntervals.internal,
            clientInstanceType: 'ExtendedChart',
            appearanceComponentType: ComponentType.extendedChart,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.xAxis,
                strings.dimensionTitles.splineAreas
            ])
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static stackedAreaDescriptor() {
        const param = {
            componentType: ComponentType.stackedArea,
            showAllTimeIntervals: ShowAllTimeIntervals.internal,
            clientInstanceType: 'ExtendedChart',
            appearanceComponentType: ComponentType.extendedChart,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.xAxis,
                strings.dimensionTitles.stackedAreas
            ])
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static stackedArea100Descriptor() {
        const param = {
            componentType: ComponentType.stackedArea100,
            showAllTimeIntervals: ShowAllTimeIntervals.internal,
            isPercentComponent: true,
            clientInstanceType: 'ExtendedChart',
            appearanceComponentType: ComponentType.extendedChart,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.xAxis,
                strings.dimensionTitles.stackedAreas100
            ])
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static stackedLineDescriptor() {
        const param = {
            componentType: ComponentType.stackedLine,
            showAllTimeIntervals: ShowAllTimeIntervals.internal,
            clientInstanceType: 'ExtendedChart',
            appearanceComponentType: ComponentType.extendedChart,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.xAxis,
                strings.dimensionTitles.stackedLines
            ])
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static stackedSplineDescriptor() {
        const param = {
            componentType: ComponentType.stackedSpline,
            showAllTimeIntervals: ShowAllTimeIntervals.internal,
            clientInstanceType: 'ExtendedChart',
            appearanceComponentType: ComponentType.extendedChart,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.xAxis,
                strings.dimensionTitles.stackedSplines
            ])
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static stackedSplineAreaDescriptor() {
        const param = {
            componentType: ComponentType.stackedSplineArea,
            showAllTimeIntervals: ShowAllTimeIntervals.internal,
            clientInstanceType: 'ExtendedChart',
            appearanceComponentType: ComponentType.extendedChart,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.xAxis,
                strings.dimensionTitles.stackedSplineAreas
            ])
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static stackedSplineArea100Descriptor() {
        const param = {
            componentType: ComponentType.stackedSplineArea100,
            showAllTimeIntervals: ShowAllTimeIntervals.internal,
            isPercentComponent: true,
            clientInstanceType: 'ExtendedChart',
            appearanceComponentType: ComponentType.extendedChart,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.xAxis,
                strings.dimensionTitles.stackedSplineAreas100
            ])
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static rangeDescriptor() {
        const param = {
            componentType: ComponentType.range,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(-1, [strings.dimensionTitles.xAxis])
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static splineRangeDescriptor() {
        const param = {
            highchartType: constants.highchartTypes.areasplinerange,
            componentType: ComponentType.splineRange,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.xAxis,
                strings.dimensionTitles.areas
            ]),
            showAllTimeIntervals: ShowAllTimeIntervals.internal,
            clientInstanceType: 'AreaRangeChart'
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static candleStickDescriptor() {
        const param = {
            componentType: ComponentType.candleStick,
            toolBarIcon: 'stickChart.png',
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(-1, [strings.dimensionTitles.xAxis]),
            changableGroup: ComponentChangeGroup.stickGroup,
            highchartType: constants.highchartTypes.candlestick
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static stickDescriptor() {
        const param = {
            componentType: ComponentType.stick,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(-1, [strings.dimensionTitles.xAxis]),
            highchartType: constants.highchartTypes.ohlc,
            changableGroup: ComponentChangeGroup.stickGroup
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static horizontalBarDescriptor() {
        const param = {
            componentType: ComponentType.horizontalBar,
            toolBarIcon: 'horizontalBarChart.png',
            seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(true, null, false),
            clientInstanceType: 'ExtendedChart',
            appearanceComponentType: ComponentType.extendedChart,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [strings.dimensionTitles.xAxis])
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static horizontalRangeBarDescriptor() {
        const param = {
            componentType: ComponentType.horizontalRangeBar,
            seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(true, null, false),
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [strings.dimensionTitles.horizontalBars]),
            highchartType: constants.highchartTypes.mirrorrange
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static horizontalGroupBarDescriptor() {
        const param = {
            componentType: ComponentType.horizontalGroupBar,
            seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(true, null, false),
            clientInstanceType: 'ExtendedChart',
            appearanceComponentType: ComponentType.extendedChart,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.groups,
                strings.dimensionTitles.horizontalBars
            ])
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static horizontalStackedBarDescriptor() {
        const param = {
            componentType: ComponentType.horizontalStackedBar,
            seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(true, null, false),
            clientInstanceType: 'ExtendedChart',
            appearanceComponentType: ComponentType.extendedChart,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.xAxis,
                strings.dimensionTitles.horizontalStackedBars
            ])
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static horizontalStackedBar100Descriptor() {
        const param = {
            componentType: ComponentType.horizontalStackedBar100,
            seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(true, null, false),
            isPercentComponent: true,
            clientInstanceType: 'ExtendedChart',
            appearanceComponentType: ComponentType.extendedChart,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.xAxis,
                strings.dimensionTitles.horizontalStackedBars100
            ])
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static processControlDescriptor() {
        const param = {
            componentType: ComponentType.processControl,
            toolBarIcon: 'processControl.png',
            canHaveDrillDown: false,
            hasChartRanges: false,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [strings.dimensionTitles.breakdown]),
            componentInstanceType: 'ProcessControlComponent'
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static chartWithRangeDescriptor() {
        const param = {
            componentType: ComponentType.chartWithRange,
            toolBarIcon: 'rangeChart.png',
            clientInstanceType: 'ChartWithRange',
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(1, [strings.dimensionTitles.xAxis]),
            highchartType: constants.highchartTypes.line
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static driverDescriptor() {
        const param = {
            componentType: ComponentType.driver,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [strings.dimensionTitles.breakdown]),
            toolBarIcon: 'filter.png',
            defaultComponentWidth: constants.descriptorConstants.defaultDriverWidth,
            defaultComponentHeight: constants.descriptorConstants.defaultDriverHeight,
            componentToolBarCommands: componentDescriptorFactory.allCommands,
            componentGroup: constants.componentGroups.driverComponents
        };
        return componentDescriptorFactory.createComponentDescriptor(param);
    }

    @enumerable
    public static entityDriverDescriptor() {
        const param = {
            componentType: ComponentType.entityDriver,
            toolBarIcon: 'entity-driver.png',
            componentToolBarCommands: [constants.commands.dataWizard, constants.commands.displayWizard, constants.commands.actionWizard],
            defaultComponentWidth: constants.descriptorConstants.defaultDriverWidth,
            defaultComponentHeight: constants.descriptorConstants.defaultDriverHeight,
            componentGroup: constants.componentGroups.driverComponents,
            componentInstanceType: 'EntityDriver'
        };
        return componentDescriptorFactory.createComponentDescriptor(param);
    }

    @enumerable
    public static dialGaugeDescriptor() {
        const param = {
            componentType: ComponentType.dialGauge,
            toolBarIcon: 'gaugeChart.png',
            highchartType: constants.highchartTypes.dialGauge
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static linearGaugeDescriptor() {
        const param = {
            componentType: ComponentType.linearGauge,
            toolBarIcon: 'linear-gauge.png',
            highchartType: constants.highchartTypes.linearGauge
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static globalFiltersDescriptor() {
        const param = {
            componentType: ComponentType.globalFilters,
            toolBarIcon: 'global-filter.png',
            componentGroup: constants.componentGroups.driverComponents
        };
        return componentDescriptorFactory.createBaseComponentDescriptor(param);
    }

    @enumerable
    public static imageDescriptor() {
        const param = {
            componentType: ComponentType.image,
            toolBarIcon: 'image.png',
            defaultComponentWidth: 200,
            defaultComponentHeight: 200,
            showTitleByDefault: false,
            componentToolBarCommands: [constants.commands.dataWizard, constants.commands.actionWizard],
            componentGroup: constants.componentGroups.uiComponents,
            componentInstanceType: 'ImageComponent'
        };
        return componentDescriptorFactory.createComponentDescriptor(param);
    }

    @enumerable
    public static labelDescriptor() {
        const param = {
            componentType: ComponentType.label,
            componentGroup: constants.componentGroups.uiComponents,
            toolBarIcon: 'text.png',
            defaultComponentWidth: 200,
            defaultComponentHeight: 150,
            showTitleByDefault: false,
            componentToolBarCommands: [constants.commands.dataWizard, constants.commands.actionWizard],
            componentInstanceType: 'LabelComponent'
        };
        return componentDescriptorFactory.createComponentDescriptor(param);
    }

    @enumerable
    public static languageSelectionDescriptor() {
        const param = {
            componentType: ComponentType.languageSelection,
            toolBarIcon: 'flags.png',
            showTitleByDefault: false,
            defaultComponentWidth: 100,
            defaultComponentHeight: 30,
            componentToolBarCommands: [constants.commands.dataWizard, constants.commands.displayWizard, constants.commands.actionWizard],
            componentInstanceType: 'LanguageSelectionComponent',
            componentGroup: constants.componentGroups.otherComponents
        };
        return componentDescriptorFactory.createComponentDescriptor(param);
    }

    @enumerable
    public static fileUploaderDescriptor() {
        const param = {
            componentType: ComponentType.fileUploader,
            toolBarIcon: 'FileUploader.png',
            showTitleByDefault: false,
            defaultComponentWidth: 100,
            defaultComponentHeight: 100,
            componentToolBarCommands: [constants.commands.dataWizard],
            componentInstanceType: 'FileUploaderComponent',
            componentGroup: constants.componentGroups.otherComponents
        };
        return componentDescriptorFactory.createComponentDescriptor(param);
    }

    @enumerable
    public static logViewerDescriptor() {
        const param = {
            componentType: ComponentType.logViewer,
            toolBarIcon: 'note.png'
        };
        return componentDescriptorFactory.createBaseComponentDescriptor(param);
    }

    @enumerable
    public static mapPinDescriptor() {
        const param = {
            clientInstanceType: 'MapPinComponent',
            componentType: ComponentType.mapPin,
            hasChartRanges: false,
            canHaveDrillDown: false,
            showAllTimeIntervals: ShowAllTimeIntervals.internal,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.items,
                strings.dimensionTitles.breakdown
            ]),
            componentInstanceType: MapPinComponent.instanceName,
            seriesSettings: {
                sparkSettingsDescriptor: { hasMainSparkSettings: true, hasAdditionalSparkSettings: false },
                hasBulletSettings: false,
                hasCellPaintingSettings: true,
                wholeRow: true
            }
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static mapDescriptor() {
        const param = {
            clientInstanceType: 'MapComponent',
            componentType: ComponentType.map,
            toolBarIcon: 'mapControlIcon.png',
            hasChartRanges: false,
            componentInstanceType: MapComponent.instanceName
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static markerGridDescriptor() {
        const param = {
            componentType: ComponentType.markerGrid,
            toolBarIcon: 'data_annotation.png',
            componentToolBarCommands: [constants.commands.actionWizard],
            componentGroup: constants.componentGroups.gridComponents
        };
        return componentDescriptorFactory.createBaseComponentDescriptor(param);
    }

    @enumerable
    public static menuDescriptor() {
        const param = {
            componentType: ComponentType.menu,
            componentGroup: constants.componentGroups.controlComponents,
            toolBarIcon: 'menu.png',
            showTitleByDefault: false,
            defaultComponentWidth: 200,
            defaultComponentHeight: 400,
            componentToolBarCommands: [constants.commands.dataWizard, constants.commands.displayWizard],
            componentInstanceType: 'MenuComponent'
        };
        return componentDescriptorFactory.createComponentDescriptor(param);
    }

    @enumerable
    public static metricGridDescriptor() {
        const param = {
            componentType: ComponentType.metricGrid,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(-1, [
                strings.dimensionTitles.rows,
                strings.dimensionTitles.columns
            ]),

            toolBarIcon: 'MetricGrid.png',
            componentToolBarCommands: componentDescriptorFactory.allCommands,
            componentGroup: constants.componentGroups.gridComponents,
            seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(
                true,
                { hasMainSparkSettings: true, hasAdditionalSparkSettings: true },
                true
            ),
            defaultDimension: constants.dimension1
        };
        param.componentDataDescriptor.canChooseCurrentDimensions = false;
        return componentDescriptorFactory.createComponentDescriptor(param);
    }

    @enumerable
    public static parameterDriverDescriptor() {
        const param = {
            componentType: ComponentType.parameterDriver,
            toolBarIcon: 'parameter-filter.png',
            defaultComponentWidth: constants.descriptorConstants.defaultDriverWidth,
            defaultComponentHeight: constants.descriptorConstants.defaultDriverHeight,
            componentToolBarCommands: [constants.commands.dataWizard, constants.commands.actionWizard],
            componentGroup: constants.componentGroups.driverComponents,
            componentInstanceType: 'ParameterDriver'
        };
        return componentDescriptorFactory.createComponentDescriptor(param);
    }

    @enumerable
    public static periodDriverDescriptor() {
        const param = {
            componentType: ComponentType.periodDriver,
            componentToolBarCommands: [constants.commands.dataWizard, constants.commands.displayWizard, constants.commands.actionWizard],
            toolBarIcon: 'period_data_32.png',
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [strings.dimensionTitles.breakdown]),
            defaultComponentWidth: constants.descriptorConstants.defaultDriverWidth,
            defaultComponentHeight: constants.descriptorConstants.defaultDriverHeight,
            componentGroup: constants.componentGroups.driverComponents,
            componentInstanceType: 'PeriodDriverComponent'
        };
        return componentDescriptorFactory.createComponentDescriptor(param);
    }

    @enumerable
    public static aggregationDriverDescriptor() {
        const param = {
            componentType: ComponentType.aggregationDriver,
            componentToolBarCommands: [constants.commands.dataWizard, constants.commands.displayWizard, constants.commands.actionWizard],
            toolBarIcon: 'aggregation.png',
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [strings.dimensionTitles.breakdown]),
            defaultComponentWidth: constants.descriptorConstants.defaultDriverWidth,
            defaultComponentHeight: constants.descriptorConstants.defaultDriverHeight,
            componentGroup: constants.componentGroups.driverComponents,
            componentInstanceType: 'AggregationDriver'
        };
        return componentDescriptorFactory.createComponentDescriptor(param);
    }

    @enumerable
    public static processGridDescriptor() {
        const param = {
            componentType: ComponentType.processGrid,
            toolBarIcon: 'ProcessGrid.png',
            canHaveDrillDown: true,
            useIntervalMatching: true
        };
        return componentDescriptorFactory.createProcessComponentDescriptor(param);
    }

    @enumerable
    public static pieDescriptor() {
        const param = {
            componentType: ComponentType.pie,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [strings.dimensionTitles.breakdown]),
            toolBarIcon: 'PieChart.png',
            hasChartRanges: false,
            highchartType: constants.highchartTypes.pie,
            hasSeriesSettingsTab: true,
            seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(true, false, false)
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static doughnutDescriptor() {
        const param = {
            componentType: ComponentType.doughnut,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [strings.dimensionTitles.breakdown]),
            toolBarIcon: 'PieChart.png',
            hasChartRanges: false,
            highchartType: constants.highchartTypes.pie,
            hasSeriesSettingsTab: true,
            hasAxisAndBreakdownsTab: true,
            hasDrillDownOptionTab: true,
            seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(true, false, false),
            clientInstanceType: 'DoughnutChart'
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static pieSemiCircleDescriptor() {
        const param = {
            componentType: ComponentType.pieSemiCircle,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [strings.dimensionTitles.breakdown]),
            toolBarIcon: 'pieSemiCircle.png',
            hasChartRanges: false,
            highchartType: constants.highchartTypes.pie,
            hasSeriesSettingsTab: true,
            hasAxisAndBreakdownsTab: true,
            hasDrillDownOptionTab: true,
            seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(true, false, false),
            clientInstanceType: 'DoughnutChart'
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static recordFormDescriptor() {
        const param = {
            componentType: ComponentType.recordForm,
            toolBarIcon: 'record-form.png',
            componentToolBarCommands: [constants.commands.dataWizard, constants.commands.timeWizard, constants.commands.actionWizard],
            defaultComponentWidth: 600,
            defaultComponentHeight: 400,
            componentGroup: constants.componentGroups.otherComponents
        };
        return componentDescriptorFactory.createComponentDescriptor(param);
    }

    @enumerable
    public static recordGridDescriptor() {
        const param = {
            componentType: ComponentType.recordGrid,
            toolBarIcon: 'RecordGrid.png',
            componentToolBarCommands: componentDescriptorFactory.allCommands,
            componentGroup: constants.componentGroups.gridComponents,
            seriesSettings: componentDescriptorFactory.seriesDescriptorSettings(true, null, false)
        };
        return componentDescriptorFactory.createComponentDescriptor(param);
    }

    @enumerable
    public static recordTimeDescriptor() {
        const param = {
            componentType: ComponentType.recordTime,
            toolBarIcon: 'record-time.png',
            canHaveDrillDown: false,
            hasChartRanges: false,
            highchartType: constants.highchartTypes.columnrange
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static reportSelfSubscriptionDescriptor() {
        const param = {
            componentType: ComponentType.reportSelfSubscription,
            toolBarIcon: 'report-subscription.png',
            defaultComponentWidth: 350,
            defaultComponentHeight: 250,
            componentGroup: constants.componentGroups.otherComponents
        };
        return componentDescriptorFactory.createBaseComponentDescriptor(param);
    }

    @enumerable
    public static scatterDescriptor() {
        const param = {
            componentType: ComponentType.scatter,
            toolBarIcon: 'scatterChart.png',
            canHaveDrillDown: false,
            hasChartRanges: false,
            highchartType: constants.highchartTypes.scatter
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static searchPanelDescriptor() {
        const param = {
            componentType: ComponentType.searchPanel,
            toolBarIcon: 'search-panel.png',
            componentToolBarCommands: [constants.commands.dataWizard, constants.commands.actionWizard],
            defaultComponentWidth: 780,
            defaultComponentHeight: 400,
            componentInstanceType: 'SearchPanel',
            componentGroup: constants.componentGroups.otherComponents
        };
        return componentDescriptorFactory.createComponentDescriptor(param);
    }

    @enumerable
    public static sparkLineDescriptor() {
        const param = {
            componentType: ComponentType.sparkLine,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [strings.dimensionTitles.breakdown]),
            toolBarIcon: 'spark.png',
            defaultComponentWidth: 200,
            defaultComponentHeight: 100,
            showTitleByDefault: false,
            canHaveDrillDown: false,
            hasChartRanges: false
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static timeDriverDescriptor() {
        const param = {
            componentType: ComponentType.timeDriver,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [strings.dimensionTitles.breakdown]),
            toolBarIcon: 'time-filter.png',
            defaultComponentWidth: constants.descriptorConstants.defaultDriverWidth,
            defaultComponentHeight: constants.descriptorConstants.defaultDriverHeight,
            componentToolBarCommands: [constants.commands.timeWizard, constants.commands.displayWizard, constants.commands.actionWizard],
            componentGroup: constants.componentGroups.driverComponents
        };
        return componentDescriptorFactory.createComponentDescriptor(param);
    }

    @enumerable
    public static timeWindowDescriptor() {
        const param = {
            componentType: ComponentType.timeWindow,
            toolBarIcon: 'time-window.png',
            componentToolBarCommands: [constants.commands.timeWizard, constants.commands.actionWizard],
            defaultComponentWidth: constants.descriptorConstants.defaultDriverWidth,
            defaultComponentHeight: constants.descriptorConstants.defaultDriverHeight,
            showTitleByDefault: false,
            componentGroup: constants.componentGroups.driverComponents
        };
        return componentDescriptorFactory.createComponentDescriptor(param);
    }

    @enumerable
    public static trafficLightDescriptor() {
        const param = {
            componentType: ComponentType.trafficLight,
            toolBarIcon: 'status-light.png',
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(-1),
            canHaveDrillDown: false,
            hasChartRanges: false,
            highchartType: constants.highchartTypes.pie
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static treeMapDescriptor() {
        const param = {
            componentType: ComponentType.treeMap,
            toolBarIcon: 'treemap.png',
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(0, [
                strings.dimensionTitles.groups,
                strings.dimensionTitles.items
            ]),
            hasChartRanges: false,
            highchartType: constants.highchartTypes.treemap
        };
        return componentDescriptorFactory.createChartDescriptor(param);
    }

    @enumerable
    public static uiLineDescriptor() {
        const param = {
            componentType: ComponentType.uiLine,
            toolBarIcon: 'UILine.png',
            showTitleByDefault: false,
            isuiComponent: true,
            componentGroup: constants.componentGroups.uiComponents
        };
        return componentDescriptorFactory.createBaseComponentDescriptor(param);
    }

    @enumerable
    public static viewHolderDescriptor() {
        const param = {
            componentType: ComponentType.viewHolder,
            componentToolBarCommands: [constants.commands.dataWizard, constants.commands.actionWizard],
            componentGroup: constants.componentGroups.controlComponents,
            toolBarIcon: 'view-holder.png',
            defaultComponentHeight: 400,
            defaultComponentWidth: 400,
            showTitleByDefault: false,
            componentInstanceType: 'ViewHolder'
        };
        return componentDescriptorFactory.createComponentDescriptor(param);
    }

    @enumerable
    public static processAgingChartDescriptor() {
        const param = {
            componentType: ComponentType.processAgingChart,
            toolBarIcon: 'processAgingChart.png',
            canHaveDrillDown: true,
            hasChartRanges: true,
            componentDataDescriptor: componentDescriptorFactory.createDataDescriptor(-1, [strings.dimensionTitles.breakdown]),
            highchartType: constants.highchartTypes.column
        };
        return componentDescriptorFactory.createProcessComponentDescriptor(param);
    }

    @enumerable
    public static processChartDescriptor() {
        const param = {
            componentType: ComponentType.processChart,
            toolBarIcon: 'processChart.png',
            canHaveDrillDown: true,
            useIntervalMatching: true,
            hasBottleneckSetting: true,
            processActions: true
        };
        return componentDescriptorFactory.createProcessComponentDescriptor(param);
    }

    @enumerable
    public static processStepChartDescriptor() {
        const param = {
            componentType: ComponentType.processStepChart,
            toolBarIcon: 'processStepChart.png',
            canHaveDrillDown: true,
            useIntervalMatching: true,
            hasBottleneckSetting: true,
            processActions: true
        };
        return componentDescriptorFactory.createProcessComponentDescriptor(param);
    }

    @enumerable
    public static swimlaneDescriptor() {
        const param = {
            componentType: ComponentType.swimlane,
            toolBarIcon: 'swimlane.png',
            useIntervalMatching: true,
            processActions: true
        };
        return componentDescriptorFactory.createProcessComponentDescriptor(param);
    }

    @enumerable
    public static processTrellisChartDescriptor() {
        const param = {
            componentType: ComponentType.processTrellisChart,
            componentToolBarCommands: [constants.commands.dataWizard, constants.commands.actionWizard],
            toolBarIcon: 'trellisChart.png',
            canHaveDrillDown: true,
            useIntervalMatching: true,
            componentInstanceType: 'ProcessTrellisChart'
        };
        return componentDescriptorFactory.createProcessComponentDescriptor(param);
    }
}
